import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  Monitor, 
  CheckCircle, 
  XCircle, 
  Clock, 
  ArrowClockwise,
  Warning
} from '@phosphor-icons/react';

const StatusBadge = ({ status }) => {
  const config = {
    success: { icon: CheckCircle, color: 'text-green-400 bg-green-400/10', label: 'OK' },
    failed: { icon: XCircle, color: 'text-red-400 bg-red-400/10', label: 'Fehler' },
    in_progress: { icon: ArrowClockwise, color: 'text-yellow-400 bg-yellow-400/10 animate-spin', label: 'Läuft' },
    pending: { icon: Clock, color: 'text-gray-400 bg-gray-400/10', label: 'Warte' },
  };
  const { icon: Icon, color, label } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${color}`}>
      <Icon size={14} weight="bold" />
      {label}
    </span>
  );
};

const OverallBadge = ({ status }) => {
  const config = {
    completed: { color: 'bg-green-500', label: 'Fertig' },
    failed: { color: 'bg-red-500', label: 'Fehler' },
    in_progress: { color: 'bg-yellow-500', label: 'Läuft' },
    pending: { color: 'bg-gray-500', label: 'Warte' },
  };
  const { color, label } = config[status] || config.pending;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${color}`}>
      {label}
    </span>
  );
};

export default function InstallStatusPage() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchStatuses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/device/install-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStatuses(data.statuses || []);
    } catch (error) {
      console.error('Error fetching install statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
    const interval = setInterval(fetchStatuses, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const filteredStatuses = statuses.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'failed') return s.overall === 'failed';
    if (filter === 'in_progress') return s.overall === 'in_progress';
    if (filter === 'completed') return s.overall === 'completed';
    return true;
  });

  const counts = {
    all: statuses.length,
    failed: statuses.filter(s => s.overall === 'failed').length,
    in_progress: statuses.filter(s => s.overall === 'in_progress').length,
    completed: statuses.filter(s => s.overall === 'completed').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Installation Status</h1>
          <p className="text-[#71717a] text-sm mt-1">Übersicht aller Geräte-Installationen</p>
        </div>
        <button
          onClick={fetchStatuses}
          className="p-2 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded transition-colors"
        >
          <ArrowClockwise size={20} />
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all', label: 'Alle', count: counts.all },
          { key: 'failed', label: 'Mit Fehlern', count: counts.failed, color: 'text-red-400' },
          { key: 'in_progress', label: 'In Arbeit', count: counts.in_progress, color: 'text-yellow-400' },
          { key: 'completed', label: 'Fertig', count: counts.completed, color: 'text-green-400' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === tab.key 
                ? 'bg-[#D50C2D] text-white' 
                : 'bg-[#141416] text-[#a1a1aa] hover:text-white border border-white/10'
            }`}
          >
            <span className={tab.color}>{tab.label}</span>
            <span className="ml-2 px-2 py-0.5 rounded bg-black/20 text-xs">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Status Cards */}
      {loading ? (
        <div className="text-center py-12 text-[#71717a]">Lade...</div>
      ) : filteredStatuses.length === 0 ? (
        <div className="text-center py-12 text-[#71717a]">
          <Monitor size={48} className="mx-auto mb-4 opacity-50" />
          <p>Keine Installationen gefunden</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredStatuses.map((status, idx) => (
            <div 
              key={idx} 
              className={`bg-[#141416] border rounded-lg p-4 ${
                status.overall === 'failed' ? 'border-red-500/50' : 'border-white/10'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1C1C1F] rounded flex items-center justify-center">
                    <Monitor size={20} className="text-[#D50C2D]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{status.computer_name}</h3>
                    <p className="text-xs text-[#52525b]">{status.tenant_code?.toUpperCase()}</p>
                  </div>
                </div>
                <OverallBadge status={status.overall} />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['teamviewer', 'rustdesk', 'anydesk', 'registration'].map(step => {
                  const stepData = status.steps?.[step] || { status: 'pending' };
                  return (
                    <div key={step} className="bg-[#0A0A0B] rounded p-3">
                      <div className="text-xs text-[#52525b] mb-1 capitalize">
                        {step === 'registration' ? 'Registrierung' : step}
                      </div>
                      <StatusBadge status={stepData.status} />
                      {stepData.message && stepData.status === 'failed' && (
                        <div className="mt-2 text-xs text-red-400 flex items-start gap-1">
                          <Warning size={12} className="mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{stepData.message}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 text-xs text-[#52525b]">
                Aktualisiert: {new Date(status.updated_at).toLocaleString('de-DE')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
